import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchSentimentWordCloud, WordCloudItem } from "../../api";
// @ts-ignore – d3-cloud types
import cloud from "d3-cloud";
import { scaleLinear } from "d3-scale";

interface Props {
  product: string;
  dateFrom?: string;
  dateTo?: string;
}

interface CloudWord {
  text: string;
  value: number;
  x: number;
  y: number;
  rotate: number;
  size: number;
}

const CLOUD_W = 560, CLOUD_H = 240;

function WordCloud({ words }: { words: WordCloudItem[] }) {
  const [laid, setLaid] = useState<CloudWord[]>([]);
  const maxV = words.length ? Math.max(...words.map(w => w.value)) : 1;
  const minV = words.length ? Math.min(...words.map(w => w.value)) : 0;

  useEffect(() => {
    if (!words.length) return;
    const sz = scaleLinear().domain([minV, maxV]).range([11, 42]);
    cloud().size([CLOUD_W, CLOUD_H])
      .words(words.map(w => ({ text: w.text, value: w.value })))
      .padding(3).rotate(() => 0).font("sans-serif")
      // @ts-expect-error -- d3-cloud types are incomplete; Word has text+size but not value
      .fontSize((d: { value?: number }) => sz(d.value ?? minV))
      .on("end", (out: CloudWord[]) => setLaid(out))
      .start();
  }, [words, minV, maxV]);

  const colorScale = scaleLinear<string>().domain([minV, maxV])
    // @ts-ignore
    .range(["#93c5fd", "#1e40af"]);

  return (
    <svg width="100%" viewBox={`0 0 ${CLOUD_W} ${CLOUD_H}`} className="overflow-visible">
      <g transform={`translate(${CLOUD_W / 2},${CLOUD_H / 2})`}>
        {laid.map((w, i) => (
          <text key={i} textAnchor="middle"
            transform={`translate(${w.x},${w.y}) rotate(${w.rotate})`}
            fontSize={w.size} fontFamily="sans-serif"
            fontWeight={w.value > maxV * 0.6 ? "700" : "400"}
            fill={colorScale(w.value) as string}
            style={{ userSelect: "none" }}>
            {w.text}
          </text>
        ))}
      </g>
    </svg>
  );
}

export default function WordCloudPanel({ product, dateFrom, dateTo }: Props) {
  const { data: words = [], isLoading, isError } = useQuery({
    queryKey: ["sentimentWordCloud", product, dateFrom ?? "", dateTo ?? ""],
    queryFn: () => fetchSentimentWordCloud(product, dateFrom, dateTo),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center rounded-xl border border-border bg-surface">
        <div className="w-2/3 space-y-2">
          {[0.8, 0.6, 0.7, 0.5].map((w, i) => (
            <div key={i} className="mx-auto h-3 animate-pulse rounded bg-surface-sunken" style={{ width: `${w * 100}%` }} />
          ))}
        </div>
      </div>
    );
  }
  if (isError || !words.length) {
    return (
      <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-border bg-surface text-[13px] text-ink-faint">
        {isError ? "词云加载失败" : "暂无关键词数据"}
      </div>
    );
  }
  return <WordCloud words={words} />;
}
